import {
  Content,
  GoogleGenAI,
  LiveCallbacks,
  LiveClientToolResponse,
  LiveConnectConfig,
  LiveServerContent,
  LiveServerMessage,
  LiveServerToolCall,
  LiveServerToolCallCancellation,
  Part,
  Session,
} from "@google/genai";

import { EventEmitter } from "eventemitter3";
import { LiveClientOptions, StreamingLog } from "../utils/types";
import { base64ToArrayBuffer } from "../utils/utils";

// Simple difference function instead of lodash
function difference<T>(array: T[], values: T[]): T[] {
  return array.filter(item => !values.includes(item));
}

/**
 * Event types that can be emitted by the MultimodalLiveClient.
 * Each event corresponds to a specific message from GenAI or client state change.
 */
export interface LiveClientEventTypes {
  // Emitted when audio data is received
  audio: (data: ArrayBuffer) => void;
  // Emitted when the connection closes
  close: (event: CloseEvent) => void;
  // Emitted when content is received from the server
  content: (data: LiveServerContent) => void;
  // Emitted when an error occurs
  error: (error: ErrorEvent) => void;
  // Emitted when the server interrupts the current generation
  interrupted: () => void;
  // Emitted for logging events
  log: (log: StreamingLog) => void;
  // Emitted when the connection opens
  open: () => void;
  // Emitted when the initial setup is complete
  setupcomplete: () => void;
  // Emitted when a tool call is received
  toolcall: (toolCall: LiveServerToolCall) => void;
  // Emitted when a tool call is cancelled
  toolcallcancellation: (
    toolcallCancellation: LiveServerToolCallCancellation
  ) => void;
  // Emitted when the current turn is complete
  turncomplete: () => void;
  // Emitted when usage metadata is received
  usage: (usage: any) => void;
  // Emitted when user transcription data is received
  userTranscription: (transcription: { text: string; finished: boolean }) => void;
  // Emitted when assistant transcription data is received
  assistantTranscription: (transcription: { text: string; finished: boolean }) => void;
}

/**
 * A event-emitting class that manages the connection to the websocket and emits
 * events to the rest of the application.
 * If you dont want to use react you can still use this.
 */
export class GenAILiveClient extends EventEmitter<LiveClientEventTypes> {
  protected client: GoogleGenAI;
  private _options: LiveClientOptions;

  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  public get status() {
    return this._status;
  }

  private _session: Session | null = null;
  public get session() {
    return this._session;
  }

  private _model: string | null = null;
  public get model() {
    return this._model;
  }

  protected config: LiveConnectConfig | null = null;

  private _resumptionHandle: string | null = null;
  public get resumptionHandle() {
    return this._resumptionHandle;
  }

  public resetResumptionHandle() {
    this._resumptionHandle = null;
  }

  private _isReconnecting: boolean = false;

  public getConfig() {
    return { ...this.config };
  }

  /**
   * Update the API key used by the client
   */
  public updateApiKey(apiKey: string) {
    // Create a new GoogleGenAI client with the updated API key
    // For ephemeral tokens, we need to use v1alpha API version
    this._options.apiKey = apiKey;
    this._options.httpOptions = { apiVersion: 'v1alpha' };
    this.client = new GoogleGenAI(this._options);
  }

  constructor(options: LiveClientOptions) {
    super();
    this._options = { ...options };
    this.client = new GoogleGenAI(options);
    this.send = this.send.bind(this);
    this.onopen = this.onopen.bind(this);
    this.onerror = this.onerror.bind(this);
    this.onclose = this.onclose.bind(this);
    this.onmessage = this.onmessage.bind(this);
  }

  protected log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  async connect(model: string, config: LiveConnectConfig, resumptionHandle?: string | null): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      return false;
    }

    this._status = "connecting";
    this.config = {
      ...config,
      sessionResumption: { handle: resumptionHandle || undefined }
    };
    this._model = model;

    const callbacks: LiveCallbacks = {
      onopen: this.onopen,
      onmessage: this.onmessage,
      onerror: this.onerror,
      onclose: this.onclose,
    };

    try {
      this._session = await this.client.live.connect({
        model,
        config: this.config,
        callbacks,
      });
    } catch (e) {
      console.error("Error connecting to GenAI Live:", e);
      this._status = "disconnected";
      return false;
    }

    this._status = "connected";
    return true;
  }

  public disconnect() {
    if (!this.session) {
      return false;
    }
    this.session?.close();
    this._session = null;
    this._status = "disconnected";
    this._resumptionHandle = null; // Clear resumption handle on manual disconnect

    this.log("client.close", `Disconnected`);
    return true;
  }

  protected onopen() {
    console.log("🔗 WebSocket connection opened");
    this.emit("open");
  }

  protected onerror(e: ErrorEvent) {
    console.log("❌ WebSocket error:", e.message);
    this.emit("error", e);
  }

  protected async onclose(e: CloseEvent) {
    console.log("🔌 WebSocket connection closed:", e.reason || "no reason");

    // Store the previous status before cleanup
    const wasConnected = this._status === "connected";

    // Clean up current session state
    this._session = null;
    this._status = "disconnected";

    this.emit("close", e);

    // Attempt automatic reconnection if we have a resumption handle and weren't manually disconnected
    if (wasConnected && this._resumptionHandle && !this._isReconnecting) {
      this._isReconnecting = true;
      this.log("client.autoReconnect", `Attempting to resume session with handle: ${this._resumptionHandle}`);
      console.log("🔄 Auto reconnection attempt with handle:", this._resumptionHandle);

      try {
        // Wait a short delay before reconnecting
        await new Promise(resolve => setTimeout(resolve, 1000));

        const success = await this.connect(this._model!, { ...this.config!, sessionResumption: { handle: undefined } }, this._resumptionHandle);
        if (success) {
          this.log("client.autoReconnect", "Successfully resumed session");
          console.log("✅ Session successfully resumed");
        } else {
          this.log("client.autoReconnect", "Failed to resume session");
          console.log("❌ Session resumption failed");
          this._resumptionHandle = null; // Clear invalid handle
        }
      } catch (error) {
        this.log("client.autoReconnect", `Error during reconnection: ${error}`);
        console.log("💥 Error during reconnection:", error);
        this._resumptionHandle = null; // Clear invalid handle
      } finally {
        this._isReconnecting = false;
      }
    }
  }

  protected async onmessage(message: LiveServerMessage) {
    // Check for usage metadata first as it can appear in any message
    if (message.usageMetadata) {
      // console.log("📊 Usage metadata received:", message.usageMetadata);
      this.emit("usage", message.usageMetadata);
    }

    // Check for transcription data in serverContent
    if (message.serverContent?.inputTranscription) {
      const transcription = {
        text: message.serverContent.inputTranscription.text || '',
        finished: message.serverContent.inputTranscription.finished || false
      };
      // console.log("🎙️ User transcription received:", transcription);
      this.emit("userTranscription", transcription);
    }
    if (message.serverContent?.outputTranscription) {
      const transcription = {
        text: message.serverContent.outputTranscription.text || '',
        finished: message.serverContent.outputTranscription.finished || false
      };
      // console.log("🎙️ Assistant transcription received:", transcription);
      this.emit("assistantTranscription", transcription);
    }

    if (message.setupComplete) {
      console.log("🚀 Setup complete received");
      this.emit("setupcomplete");
      return;
    }
    if (message.sessionResumptionUpdate) {
      console.log("🔄 Session resumption update:", message.sessionResumptionUpdate);
      if (message.sessionResumptionUpdate.resumable && message.sessionResumptionUpdate.newHandle) {
        this._resumptionHandle = message.sessionResumptionUpdate.newHandle;
        console.log("💾 Updated resumption handle:", this._resumptionHandle);
      }
      return;
    }
    if (message.toolCall) {
      console.log("🔧 Tool call received:", message.toolCall);
      this.emit("toolcall", message.toolCall);
      return;
    }
    if (message.toolCallCancellation) {
      console.log("❌ Tool call cancellation received:", message.toolCallCancellation);
      this.emit("toolcallcancellation", message.toolCallCancellation);
      return;
    }

    // this json also might be `contentUpdate { interrupted: true }`
    // or contentUpdate { end_of_turn: true }
    if (message.serverContent) {
      const { serverContent } = message;
      if ("interrupted" in serverContent) {
        console.log("⏹️ Server interrupted the response");
        this.emit("interrupted");
        return;
      }
      if ("turnComplete" in serverContent) {
        // console.log("✅ Turn complete received");
        this.emit("turncomplete");
      }

      if ("modelTurn" in serverContent) {
        let parts: Part[] = serverContent.modelTurn?.parts || [];

        // when its audio that is returned for modelTurn
        const audioParts = parts.filter(
          (p) => p.inlineData && p.inlineData.mimeType?.startsWith("audio/pcm")
        );
        const base64s = audioParts.map((p) => p.inlineData?.data);

        // strip the audio parts out of the modelTurn
        const otherParts = difference(parts, audioParts);
        // console.log("otherParts", otherParts);

        base64s.forEach((b64) => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            this.emit("audio", data);
          }
        });
        if (!otherParts.length) {
          return;
        }

        parts = otherParts;

        const content: { modelTurn: Content } = { modelTurn: { parts } };
        // console.log(`📝 Content received with ${parts.length} parts`);
        this.emit("content", content);
      }
    } else {
      console.log("received unmatched message", message);
    }
  }

  /**
   * send realtimeInput, this is base64 chunks of "audio/pcm" and/or "image/jpg"
   */
  sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    let hasAudio = false;
    let hasVideo = false;
    for (const ch of chunks) {
      this.session?.sendRealtimeInput({ media: ch });
      if (ch.mimeType.includes("audio")) {
        hasAudio = true;
      }
      if (ch.mimeType.includes("image")) {
        hasVideo = true;
      }
      if (hasAudio && hasVideo) {
        break;
      }
    }
    const message =
      hasAudio && hasVideo
        ? "audio + video"
        : hasAudio
        ? "audio"
        : hasVideo
        ? "video"
        : "unknown";
    this.log(`client.realtimeInput`, message);
  }

  /**
   *  send a response to a function call and provide the id of the functions you are responding to
   */
  sendToolResponse(toolResponse: LiveClientToolResponse) {
    if (
      toolResponse.functionResponses &&
      toolResponse.functionResponses.length
    ) {
      this.session?.sendToolResponse({
        functionResponses: toolResponse.functionResponses,
      });
      this.log(`client.toolResponse`, toolResponse);
    }
  }

  /**
   * send normal content parts such as { text }
   */
  send(parts: Part | Part[], turnComplete: boolean = true) {
    this.session?.sendClientContent({ turns: parts, turnComplete });
    this.log(`client.send`, {
      turns: Array.isArray(parts) ? parts : [parts],
      turnComplete,
    });
  }
}
